// src/utils/introTour.js

import introJs from "intro.js";
import "intro.js/introjs.css";
import "./introTour.css";
import { addClassToElement } from "shared/utils/addClassToElement";

export function initializeIntroTour({
  isAppReady,
  tourHomePage,
  tourGlobalNavbar,
  tourGlobalNavbarTitle,
  tourHomeHelpPopup,
  tourHomeHelpPopupTitle,
  tourGlobalAllFilters,
  tourGlobalAllFiltersTitle,
  tourGlobalCurrentSelections,
  tourGlobalCurrentSelectionsTitle,
  tourGlobalBookmarkCreate,
  tourGlobalBookmarkCreateTitle,
  tourGlobalBookmarkShare,
  tourGlobalBookmarkShareTitle,
  tourHomeBarchart,
  tourHomeBarchartTitle,
  tourGlobalFeedback,
  tourGlobalFeedbackTitle,
  tourParamsNext,
  tourParamsPrev,
  tourParamsDone,
  tourParamsOf,
  tourParamsTourStartButton,
  elementClassMappings = [],
}) {
  // Check if the user already completed the tour
  const hasCompletedTour = localStorage.getItem("hasCompletedTour");

  // Media query (desktop only)
  const mediaQuery = window.matchMedia("(min-width: 1200px)");

  // Instead of matching the full href, check the hash fragment:
  // when HashRouter is at home, window.location.hash === "#/"
  const isHomeHash = window.location.hash === "#/" || window.location.hash === "";

  // No search params (clean URL)
  const isEmptySearch = window.location.search === "";

  // Log all boolean conditions for debugging
  console.log("[introTour] isAppReady:", isAppReady);
  console.log("[introTour] mediaQuery.matches:", mediaQuery.matches);
  console.log("[introTour] isHomeHash:", isHomeHash);
  console.log("[introTour] isEmptySearch:", isEmptySearch);
  console.log("[introTour] hasCompletedTour:", hasCompletedTour);

  // If any condition fails, abort automatic tour start
  if (
    hasCompletedTour ||
    !isAppReady ||
    !mediaQuery.matches ||
    !isHomeHash ||
    !isEmptySearch
  ) {
    console.log(
      "[introTour] Aborting auto-start due to:",
      {
        isAppReadyFail: !isAppReady,
        mediaQueryFail: !mediaQuery.matches,
        notHomeHash: !isHomeHash,
        nonEmptySearch: !isEmptySearch,
        alreadyCompleted: !!hasCompletedTour,
      }
    );
    return;
  }

  // build all your wait-and-class promises
  const mappingPromises = elementClassMappings.map(
    ({ selector, className, timeoutMs }) =>
      addClassToElement(selector, className, timeoutMs)
  );

  // once all (or none) have settled, start the tour
  Promise.allSettled(mappingPromises).then(() => {
      const tour = introJs();

      tour.setOptions({
        steps: [
          {
            element: ".tour-homepage-page",
            intro: tourHomePage,
            tooltipClass: "tour-homepage-page-popup-position",
          },
          {
            element: ".tour-global-sidebar",
            intro: tourGlobalNavbar,
            title: tourGlobalNavbarTitle,
          },
          {
            element: ".tour-homepage-help-popup",
            intro: tourHomeHelpPopup,
            title: tourHomeHelpPopupTitle,
            tooltipClass: "tour-homepage-help-popup-position",
          },
          {
            element: ".tour-global-filterpane-dropdown",
            intro: tourGlobalAllFilters,
            title: tourGlobalAllFiltersTitle,
          },
          {
            element: ".tour-global-current-selections",
            intro: tourGlobalCurrentSelections,
            title: tourGlobalCurrentSelectionsTitle,
          },
          {
            element: ".tour-global-bookmark-create",
            intro: `<div>
                      <p>${tourGlobalBookmarkCreate}</p>
                      <img src="${process.env.PUBLIC_URL}/tour_global_bookmark_create.gif"
                           alt="Login and create bookmark"
                           style="width:509px; height:240px; border:0;">
                    </div>`,
            title: tourGlobalBookmarkCreateTitle,
            tooltipClass: "tour-global-bookmark-create-style",
          },
          {
            element: ".tour-global-bookmark-share",
            intro: `<div>
                      <p>${tourGlobalBookmarkShare}</p>
                      <img src="${process.env.PUBLIC_URL}/tour_global_bookmark_share.gif"
                           alt="Share bookmark"
                           style="width:509px; height:240px; border:0;">
                    </div>`,
            title: tourGlobalBookmarkShareTitle,
            tooltipClass: "custom-tour-global-bookmark-share-position",
          },
          {
            element: ".tour-home-barchart",
            intro: tourHomeBarchart,
            title: tourHomeBarchartTitle,
            tooltipClass: "tour-home-barchart-position",
          },
          {
            element: ".tour-global-feedback",
            intro: tourGlobalFeedback,
            title: tourGlobalFeedbackTitle,
            tooltipClass: "tour-global-feedback-position",
            keyboardNavigation: false,
          },
        ],
        showBullets: true,
        showStepNumbers: true,
        nextLabel: tourParamsNext,
        prevLabel: tourParamsPrev,
        doneLabel: tourParamsDone,
        stepNumbersOfLabel: tourParamsOf,
        helperElementPadding: 0,
        dontShowAgain: false,
        autoPosition: true,
        scrollToElement: false,
      });

      tour.oncomplete(() => {
        console.log("[introTour] Tour completed");
        localStorage.setItem("hasCompletedTour", "true");
      });

      tour.onexit(() => {
        console.log("[introTour] Tour exited");
        localStorage.setItem("hasCompletedTour", "true");
      });

      tour.start();
    })
    .catch(() => {
      // If waiting for the button failed (timeout), do nothing further
    });
}
